// Authored HTML -> plain text, the thing the client actually pastes into their
// mail tool. Moved out of email-copy.ts (2026-08-06) so the copy FIELD inside
// the renderer can produce byte-identical text without importing the e-mail
// module, which imports the renderer.
//
// The contract is unchanged and is the reason this file exists at all: reading
// order, every word kept, no reader chrome. A copy button that pastes
// something other than what the reader saw is worse than no copy button.

import { childrenOf, docElements, docText, parseDocHtml, isInlineOnly, type DocNode } from "./doc-parse";

/** An element node: everything the structural reports below reason about. */
type DocElement = Extract<DocNode, { kind: "el" }>;
import { collectSegments, segmentDef, segmentKey, type SegmentMap } from "./doc-segments";
import { docT } from "./doc-locale";

/**
 * `<gap kind="merge">jméno</gap>` pastes as the mail-tool tag `{{jmeno}}`
 * (blueprint 4.3): the TOOL substitutes it, so the paste keeps machine
 * syntax - ascii slug, no spaces - never a warm placeholder asking the
 * client to type something by hand.
 */
function mergeTagPlain(text: string): string {
  const slug = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().replace(/\s+/g, "_");
  return `{{${slug || "hodnota"}}}`;
}

function inlinePlain(nodes: DocNode[], segments: SegmentMap): string {
  return nodes
    .map((node) => {
      if (node.kind === "text") return node.text;
      // `<gap>` is a field the client fills in. The DOPLNIT prefix is how it
      // stays visible after the paste: an unfilled gap in a sent e-mail must
      // be impossible to miss, and the reader saw it highlighted. The merge
      // kind is the mail tool's field instead and keeps tool syntax.
      if (node.tag === "gap") {
        const body = inlinePlain(node.children, segments);
        return node.attrs.kind === "merge" ? mergeTagPlain(body) : `[DOPLNIT: ${body}]`;
      }
      // `<term seg="S1"/>` with no body prints the segment's own label. The map
      // comes from the WHOLE document, so a fold copied on its own still reads
      // "Seznam" and never the raw key.
      if (node.tag === "term" && node.children.length === 0) {
        return segmentDef(segments, node.attrs.seg)?.label ?? "";
      }
      return inlinePlain(node.children, segments);
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

function nodePlain(node: DocNode, out: string[], segments: SegmentMap): void {
  if (node.kind === "text") {
    const text = node.text.trim();
    if (text) out.push(text);
    return;
  }
  const attrs = node.attrs;
  switch (node.tag) {
    case "p": case "lead": case "scene": case "punch": case "q": case "quote":
      out.push(inlinePlain(node.children, segments)); break;
    case "h": case "doc-card-title":
      out.push(inlinePlain(node.children, segments)); break;
    case "term": case "gap":
      out.push(inlinePlain([node], segments)); break;
    case "chapter":
      if (attrs.title) out.push(attrs.title);
      if (attrs.takeaway) out.push(attrs.takeaway);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "card":
      if (attrs.title) out.push(attrs.title);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "fold":
      if (attrs.title) out.push(attrs.title);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "copyfield": {
      // A one-line field IS its label ("Předmět: ..."), the way the reader sees
      // it and the way a mail tool wants it. A field with block content is an
      // e-mail BODY: prefixing it with "Tělo e-mailu:" would corrupt every
      // paste, so the label stays on screen and out of the clipboard.
      const value = inlinePlain(node.children, segments);
      if (isInlineOnly(node.children)) {
        out.push(attrs.label ? `${attrs.label}: ${value}` : value);
      } else {
        node.children.forEach((child) => nodePlain(child, out, segments));
      }
      break;
    }
    case "segments":
      if (attrs.title) out.push(attrs.title);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "seg": {
      const label = attrs.label ?? attrs.name ?? "";
      if (label) out.push(attrs.tip ? `${label}: ${attrs.tip}` : label);
      break;
    }
    case "timeline":
      if (attrs.title) out.push(attrs.title);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "beat": {
      const head = [attrs.day, attrs.label].filter(Boolean).join(" · ");
      const note = inlinePlain(node.children, segments);
      const line = [head, note].filter(Boolean).join(" · ");
      if (line) out.push(line);
      break;
    }
    // The e-mail blueprint tags (LAYOUT-BLUEPRINT-EMAILS 4.1/4.2). The copy
    // FIELDS pass a child's children directly, so these cases serve the
    // whole-document conversion: labels the reader saw, values verbatim.
    case "email": {
      const head = [attrs.n, attrs.name].filter(Boolean).join(" · ");
      const meta = [attrs.day, attrs.time, attrs.auto].filter(Boolean).join(" · ");
      if (head) out.push(head);
      if (meta) out.push(meta);
      if (attrs.task) out.push(attrs.task);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    }
    case "subject":
      out.push(`${docT("doc.email.subject")}: ${inlinePlain(node.children, segments)}`); break;
    case "alt":
      out.push(`${docT("doc.email.altSubject")}: ${inlinePlain(node.children, segments)}`); break;
    case "preheader":
      out.push(`${docT("doc.email.preheader")}: ${inlinePlain(node.children, segments)}`); break;
    case "phase": {
      const head = [attrs.label, attrs.note].filter(Boolean).join(" · ");
      if (head) out.push(head);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    }
    case "tl": {
      const line = [attrs.day, attrs.n, attrs.name].filter(Boolean).join(" · ");
      if (line) out.push(line);
      break;
    }
    case "item":
      out.push(`- ${inlinePlain(node.children, segments)}`); break;
    case "stat": case "meter":
      out.push(`${attrs.label ?? ""}: ${attrs.value ?? ""}${attrs.note ? ` (${attrs.note})` : ""}`); break;
    case "step": {
      const head = [attrs.label, attrs.title].filter(Boolean).join(": ");
      if (head) out.push(head);
      const detail = attrs.detail ?? inlinePlain(node.children, segments);
      if (detail) out.push(detail);
      break;
    }
    case "hero":
      out.push([attrs.value, attrs.claim].filter(Boolean).join(" "));
      if (attrs.note) out.push(attrs.note);
      break;
    case "compare": {
      if (attrs.left) out.push(attrs.left);
      node.children.filter((child) => child.kind === "el" && child.tag === "neg")
        .forEach((child) => { if (child.kind === "el") out.push(`- ${inlinePlain(child.children, segments)}`); });
      if (attrs.right) out.push(attrs.right);
      node.children.filter((child) => child.kind === "el" && child.tag === "pos")
        .forEach((child) => { if (child.kind === "el") out.push(`- ${inlinePlain(child.children, segments)}`); });
      break;
    }
    case "table": {
      const head = (attrs.head ?? "").split("|").map((cell) => cell.trim()).filter(Boolean);
      if (head.length) out.push(head.join(" | "));
      node.children.filter((child) => child.kind === "el" && child.tag === "row")
        .forEach((child) => { if (child.kind === "el") out.push(inlinePlain(child.children, segments).split("|").map((cell) => cell.trim()).join(" | ")); });
      break;
    }
    case "swatch":
      out.push([attrs.name, attrs.hex, attrs.use].filter(Boolean).join(" ")); break;
    // The webinar tags (F91). Reading order, every word kept: the funnel's
    // goals and numbers, the wireframe's real copy, the evening's phases and
    // their minute rows all survive the paste and the PDF export.
    case "funnel":
      if (attrs.title) out.push(attrs.title);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "fstage": {
      const head = [attrs.name, attrs.goal ? `${docT("doc.funnel.goal")}: ${attrs.goal}` : "", attrs.num]
        .filter(Boolean).join(" · ");
      if (head) out.push(head);
      if (attrs.note) out.push(attrs.note);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    }
    case "wireframe":
      if (attrs.name) out.push(attrs.name);
      if (attrs.note) out.push(attrs.note);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "wf-pretitle": case "wf-title": case "wf-sub": case "wf-note": case "wf-cta":
      out.push(inlinePlain(node.children, segments)); break;
    case "wf-form": {
      const fields = (attrs.fields ?? "").split("|").map((field) => field.trim()).filter(Boolean);
      if (fields.length) out.push(`${docT("doc.wf.form")}: ${fields.join(" | ")}`);
      if (attrs.button) out.push(`${docT("doc.wf.button")}: ${attrs.button}`);
      if (attrs.note) out.push(attrs.note);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    }
    case "wf-video":
      out.push(attrs.label ?? inlinePlain(node.children, segments)); break;
    case "evening":
      if (attrs.title || attrs.total) out.push([attrs.title, attrs.total].filter(Boolean).join(" · "));
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "ph": {
      const head = [
        [attrs.n, attrs.name].filter(Boolean).join(". "),
        attrs.start,
        attrs.min
      ].filter(Boolean).join(" · ");
      if (head) out.push(head);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    }
    case "minutes":
      if (attrs.title) out.push(attrs.title);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "mi": {
      const body = inlinePlain(node.children, segments);
      out.push(attrs.at ? `${attrs.at}: ${body}` : body);
      if (attrs.eg) out.push(`${docT("doc.mi.example")}: ${attrs.eg}`);
      break;
    }
    // F93: the seam, the arc and the promise scope. Attribute-carried text is
    // the whole point of these tags, so the plain conversion has to read the
    // attributes or a paste loses the návaznost and the offer promise.
    case "seam":
      if (attrs.before) out.push(`${docT("doc.seam.before")}: ${attrs.before}`);
      if (attrs.after) out.push(`${docT("doc.seam.after")}: ${attrs.after}`);
      break;
    case "arc":
      if (attrs.title) out.push(attrs.title);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    case "move": {
      const body = inlinePlain(node.children, segments);
      out.push([attrs.n, attrs.label].filter(Boolean).join(". "));
      if (body) out.push(body);
      break;
    }
    case "scope": {
      if (attrs.title) out.push(attrs.title);
      const covers = (attrs.covers ?? "").split("|").map((area) => area.trim()).filter(Boolean);
      if (covers.length) out.push(`${docT("doc.scope.covers")}: ${covers.join(" | ")}`);
      if (attrs.offer) out.push(`${docT("doc.scope.offer")}: ${attrs.offer}`);
      node.children.forEach((child) => nodePlain(child, out, segments));
      break;
    }
    // F95: the calculator. A paste and a PDF have no inputs, so what travels
    // is what the reader saw BEFORE touching anything: the starting numbers,
    // every prefilled range, the tariffs and the formula that joins them. The
    // formula matters most - without it the numbers are four loose figures.
    case "calc": {
      if (attrs.title) out.push(attrs.title);
      if (attrs.visits) out.push(`${docT("doc.calc.visits")}: ${attrs.visits}`);
      node.children.forEach((child) => nodePlain(child, out, segments));
      out.push(docT("doc.calc.formula"));
      break;
    }
    case "aud": {
      if (attrs.name) out.push(attrs.name);
      if (attrs.reg) out.push(`${docT("doc.calc.rateReg")}: ${attrs.reg} % (${docT("doc.calc.baseVisits")})`);
      if (attrs.show) out.push(`${docT("doc.calc.rateShow")}: ${attrs.show} % (${docT("doc.calc.baseRegistrations")})`);
      if (attrs.buy) out.push(`${docT("doc.calc.rateBuy")}: ${attrs.buy} % (${docT("doc.calc.baseAttended")})`);
      if (attrs.cpv) out.push(`${docT("doc.calc.cpv")}: ${attrs.cpv}`);
      if (attrs.note) out.push(attrs.note);
      break;
    }
    case "tariff": {
      const mark = attrs.mark !== undefined && attrs.mark !== "false"
        ? ` (${docT("doc.calc.recommended")})` : "";
      out.push(`${[attrs.name, attrs.price].filter(Boolean).join(": ")}${mark}`);
      break;
    }
    case "divider":
      if (attrs.kicker) out.push(attrs.kicker); break;
    case "figure":
      if (attrs.caption) out.push(attrs.caption); break;
    default:
      node.children.forEach((child) => nodePlain(child, out, segments));
  }
}

/**
 * Authored HTML (whole doc or a fold fragment) -> plain text for the mail tool,
 * reading order, every word kept.
 *
 * `segments` is optional and matters for FRAGMENTS: a single fold does not
 * contain the `<seg>` declarations that live in the document's legend, so a
 * caller that split a document passes the map it built from the whole source.
 */
export function docHtmlToPlainText(source: string, segments?: SegmentMap): string {
  const nodes = parseDocHtml(source);
  const map = segments ?? collectSegments(nodes);
  const out: string[] = [];
  nodes.forEach((node) => nodePlain(node, out, map));
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** Plain text of an already-parsed subtree, for the in-document copy fields. */
export function docNodesToPlainText(nodes: DocNode[], segments: SegmentMap): string {
  const out: string[] = [];
  nodes.forEach((node) => nodePlain(node, out, segments));
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

export interface DocInteractiveReport {
  /** Fold ids a timeline beat points at. */
  beatTargets: string[];
  /** Ids actually declared by a `<fold id="...">`. */
  foldIds: string[];
  /** Beat targets with no fold behind them: those beats must not be buttons. */
  missingTargets: string[];
  foldCount: number;
  /** Folds that contain at least one `<copyfield>`. */
  foldsWithCopyField: number;
  copyFieldCount: number;
}

/**
 * Structural read of an interactive document, for the guards.
 *
 * The bug this answers (Pavel 2026-08-06): "první e-mail nejde rozkliknout. On
 * tam vlastně není" - the first e-mail's subject sat loose above the folds, so
 * the reader saw a preview with nothing to open while every other e-mail had a
 * fold. A renderer cannot invent the missing fold, but the mismatch is
 * MEASURABLE: an e-mail document with N e-mails on its timeline has N folds,
 * each with copy fields, and no beat pointing into thin air.
 */
export function docInteractiveReport(source: string): DocInteractiveReport {
  const elements = docElements(parseDocHtml(source));
  const folds = elements.filter((node) => node.tag === "fold");
  const foldIds = folds.map((fold) => (fold.attrs.id ?? "").trim()).filter(Boolean);
  const beatTargets = elements
    .filter((node) => node.tag === "beat")
    .map((beat) => (beat.attrs.to ?? "").trim())
    .filter(Boolean);
  const known = new Set(foldIds);
  return {
    beatTargets,
    foldIds,
    missingTargets: beatTargets.filter((target) => !known.has(target)),
    foldCount: folds.length,
    foldsWithCopyField: folds.filter((fold) => docElements(fold.children).some((node) => node.tag === "copyfield")).length,
    copyFieldCount: elements.filter((node) => node.tag === "copyfield").length
  };
}

export interface EmailBlueprintReport {
  /** `n` of every `<email>` card, in document order. */
  emailNs: string[];
  /** `n` of every `<tl>` map row, in document order. */
  tlNs: string[];
  /** Map rows pointing at an e-mail card that does not exist: dead rows. */
  rowsWithoutCard: string[];
  /** E-mail cards the map never lists: unreachable from the map. */
  cardsWithoutRow: string[];
  /** An `n` used by two cards: the second card's id silently collides. */
  duplicateNs: string[];
  /** Cards whose `<subject>` is missing or empty. */
  emptySubjects: string[];
  /** Cards whose `<body>` is missing or empty. */
  emptyBodies: string[];
}

/**
 * The R6 parity read (blueprint 7): the set of `<tl n>` equals the set of
 * `<email n>`, matched by `n`, each card with a non-empty subject and body.
 * The measurable form of "první e-mail nejde rozkliknout": a map row without
 * a card, or a card the map never reaches, cannot ship unnoticed.
 */
export function emailBlueprintReport(source: string): EmailBlueprintReport {
  const elements = docElements(parseDocHtml(source));
  const emails = elements.filter((node) => node.tag === "email");
  const rows = elements.filter((node) => node.tag === "tl");
  const n = (node: Extract<DocNode, { kind: "el" }>) => (node.attrs.n ?? "").trim();
  const emailNs = emails.map(n).filter(Boolean);
  const tlNs = rows.map(n).filter(Boolean);
  const cards = new Set(emailNs);
  const listed = new Set(tlNs);
  const text = (email: Extract<DocNode, { kind: "el" }>, tag: string) =>
    docText(childrenOf(email, tag).flatMap((child) => child.children));
  return {
    emailNs,
    tlNs,
    rowsWithoutCard: [...new Set(tlNs.filter((value) => !cards.has(value)))],
    cardsWithoutRow: emailNs.filter((value) => !listed.has(value)),
    duplicateNs: emailNs.filter((value, index) => emailNs.indexOf(value) !== index),
    emptySubjects: emails.filter((email) => !text(email, "subject")).map(n),
    emptyBodies: emails.filter((email) => !text(email, "body")).map(n)
  };
}

export interface WebinarDocReport {
  funnelStages: number;
  /** `<fstage to>` targets with nothing behind them: those stages must not be buttons. */
  funnelDeadTargets: string[];
  wireframes: number;
  /** Wireframes with no `<wf-title>` content: a page mockup without its headline. */
  wireframesWithoutTitle: number;
  eveningPhases: number;
  /** Phase `n`s used twice: the second section's id silently collides. */
  duplicatePhaseNs: string[];
  /** Phases with no `<minutes>` axis at all (F93: every phase carries one). */
  phasesWithoutMinutes: string[];
  /** Phases whose step count is below the floor for their own minutes. */
  shallowPhases: string[];
  /** Phases with no `<seam>`: the reader cannot see what follows from what. */
  phasesWithoutSeam: string[];
  /** Phases where no step carries an `eg` example. */
  phasesWithoutExample: string[];
  minuteRows: number;
  /** Steps written as a label instead of an instruction ("3 slova"). */
  thinMinuteRows: number;
  arcMoves: number;
  /** Areas the sold product covers, declared by `<scope covers>`. */
  scopeCovers: number;
  /** `<calc>` blocks in the document (F95: exactly one, in the funnel chapter). */
  calcBlocks: number;
  /** Does a `<calc>` stand in the same chapter as the `<funnel>` it models? */
  calcBesideFunnel: boolean;
  /** Audience keys whose three rates all parse: the ones the tool can use. */
  calcAudiences: string[];
  /** Audiences dropped for a missing or unparsable rate. */
  calcAudiencesIncomplete: string[];
  /** Audiences the ad money is counted for (the paid traffic one). */
  calcPaidAudiences: string[];
  /** Tariffs with a usable price: without one there is no money band. */
  calcTariffs: number;
}

/**
 * How many minute steps a phase of `minutes` owes the reader. Pavel
 * 2026-08-07: "pro každých 5 minut mi dáváš 3 slova... potřebuju vědět, co mám
 * dát do každé minuty." Two steps minimum, then one step per 2.5 minutes, so a
 * 15-minute phase owes six and a 35-minute block owes fourteen. The Python
 * gate carries the same formula; changing one without the other splits the
 * contract in half.
 */
export function minuteStepFloor(minutes: number): number {
  return Math.max(2, Math.ceil((minutes * 2) / 5));
}

/** A step of three words is a category name, not an instruction. */
const THIN_STEP_WORDS = 5;

/**
 * Structural read of a webinar document, for the guards (F91). The measurable
 * half of Pavel's asks: the funnel is drawn, every wireframe carries a real
 * headline, the evening has phases, and a phase long enough to get lost in
 * (9+ minutes) carries its own minute axis.
 */
export function webinarDocReport(source: string): WebinarDocReport {
  const nodes = parseDocHtml(source);
  const elements = docElements(nodes);
  const ids = new Set(
    elements
      .map((node) => (
        node.tag === "fold" ? (node.attrs.id ?? "").trim()
          : node.tag === "email" ? `email-${(node.attrs.n ?? "").trim()}`
            : node.tag === "wireframe" ? (node.attrs.id ?? "").trim()
              : undefined
      ))
      .filter((id): id is string => Boolean(id))
  );
  const evenings = elements.filter((node) => node.tag === "evening");
  const phNodes = evenings.flatMap((evening) => childrenOf(evening, "ph"));
  phNodes.forEach((ph, index) => { if (!(ph.attrs.n ?? "").trim()) ph.attrs.n = String(index + 1); });
  phNodes.forEach((ph) => ids.add(`ph-${(ph.attrs.n ?? "").trim()}`));
  const phNs = phNodes.map((ph) => (ph.attrs.n ?? "").trim());
  const stages = elements.filter((node) => node.tag === "fstage");
  const wireframes = elements.filter((node) => node.tag === "wireframe");
  const minutesOf = (ph: DocNode): number => {
    const match = /\d+/.exec(ph.kind === "el" ? ph.attrs.min ?? "" : "");
    return match ? Number(match[0]) : 0;
  };
  const label = (ph: DocElement): string =>
    [(ph.attrs.n ?? "").trim(), ph.attrs.name ?? ""].filter(Boolean).join(". ");
  const stepsOf = (ph: DocElement): DocElement[] =>
    childrenOf(ph, "minutes").flatMap((block) => childrenOf(block, "mi"));
  const allSteps = elements.filter((node) => node.tag === "mi");
  return {
    funnelStages: stages.length,
    funnelDeadTargets: stages
      .map((stage) => (stage.attrs.to ?? "").trim())
      .filter((target) => target && !ids.has(target)),
    wireframes: wireframes.length,
    wireframesWithoutTitle: wireframes.filter(
      (frame) => !docText(childrenOf(frame, "wf-title").flatMap((child) => child.children))
    ).length,
    eveningPhases: phNodes.length,
    duplicatePhaseNs: phNs.filter((value, index) => value && phNs.indexOf(value) !== index),
    phasesWithoutMinutes: phNodes.filter((ph) => stepsOf(ph).length === 0).map(label),
    shallowPhases: phNodes
      .filter((ph) => {
        const steps = stepsOf(ph).length;
        return steps > 0 && steps < minuteStepFloor(minutesOf(ph));
      })
      .map(label),
    phasesWithoutSeam: phNodes.filter((ph) => childrenOf(ph, "seam").length === 0).map(label),
    phasesWithoutExample: phNodes
      .filter((ph) => !stepsOf(ph).some((step) => (step.attrs.eg ?? "").trim()))
      .map(label),
    minuteRows: allSteps.length,
    thinMinuteRows: allSteps.filter(
      (step) => docText(step.children).trim().split(/\s+/).filter(Boolean).length < THIN_STEP_WORDS
    ).length,
    arcMoves: elements.filter((node) => node.tag === "move").length,
    scopeCovers: elements
      .filter((node) => node.tag === "scope")
      .reduce((count, node) => count + (node.attrs.covers ?? "").split("|").filter((area) => area.trim()).length, 0),
    ...calcReport(elements)
  };
}

/**
 * The calculator's structural half (F95). The renderer already refuses to draw
 * an audience without rates, so what the guards need is the count of what
 * SURVIVED that refusal: a document whose second audience quietly vanished
 * looks fine on screen and is missing the whole cold-traffic half of Pavel's
 * ask, which is exactly the class of defect a render cannot show.
 */
function calcReport(elements: DocElement[]): Pick<
  WebinarDocReport,
  "calcBlocks" | "calcBesideFunnel" | "calcAudiences" | "calcAudiencesIncomplete"
  | "calcPaidAudiences" | "calcTariffs"
> {
  const range = (raw: string | undefined): boolean => /\d/.test(raw ?? "");
  const auds = elements.filter((node) => node.tag === "aud");
  const name = (node: DocElement, index: number) =>
    (node.attrs.key ?? node.attrs.name ?? `a${index + 1}`).trim();
  const complete = (node: DocElement) =>
    range(node.attrs.reg) && range(node.attrs.show) && range(node.attrs.buy);
  const chapterHolds = (node: DocNode, tag: string): boolean =>
    node.kind === "el"
    && (node.tag === tag || node.children.some((child) => chapterHolds(child, tag)));
  const chapters = elements.filter((node) => node.tag === "chapter");
  return {
    calcBlocks: elements.filter((node) => node.tag === "calc").length,
    calcBesideFunnel: chapters.some(
      (chapter) => chapterHolds(chapter, "funnel") && chapterHolds(chapter, "calc")
    ),
    calcAudiences: auds.filter(complete).map(name),
    calcAudiencesIncomplete: auds.filter((node) => !complete(node)).map(name),
    calcPaidAudiences: auds
      .filter((node) => node.attrs.ads !== undefined
        && !["false", "ne", "0"].includes((node.attrs.ads || "").toLowerCase()))
      .map(name),
    calcTariffs: elements.filter(
      (node) => node.tag === "tariff"
        && Number((node.attrs.price ?? "").replace(/\s/g, "").replace(",", ".")) > 0
    ).length
  };
}

export { segmentKey };
